/**
 * Designer Search Plugin
 * Creates a searchable dialog with typeahead functionality for designer brands
 * @module elements/fields/designerSearch
 */
import $ from 'jquery';
import registerJQueryPlugin from 'styleguide/lib/register-jquery-plugin';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import Keyboard from 'styleguide/modules/keyboard';
import Bloodhound from 'vendor/typeahead';

// Plugin constants
export const PLUGIN_NAME = 'searchDesigner';
export const DEFAULTS = {
    searchDialog: '.js-search-dialog',
    typeaheadInput: '#search-designer-input',
    closeButton: '.js-search-dialog-close',
    clearButton: '.js-search-dialog-clear',
    submitButton: '.js-search-dialog-submit',
};

/**
 * Initializes the search typeahead functionality
 * @param {jQuery} $el - The element that triggers the search dialog
 * @param {Object} opts - Configuration options
 */
const initSearch = ($el, opts) => {
    const { typeaheadInput } = opts;

    // Designer brands data with metadata
    const designerBrands = window.rcProps.searchDesignerBrands;

    // JSON strings for recent and recommended searches
    const recentSearchJson = window.rcProps.searchDesignerRecentSearches;
    const recommendedSearchJson = window.rcProps.searchDesignerRecommendations;


    /**
     * Finds the closest matching brand suggestion for a given query using Levenshtein distance
     * @param {string} query - The search query
     * @returns {string|null} - The closest match or null if none found
     */
    function findClosestMatch(query) {
        if (!query || query.length < 2) return null;

        let bestMatch = null;
        let bestDistance = Infinity;

        designerBrands.forEach(function (brand) {
            const distance = levenshteinDistance(query.toLowerCase(), brand.suggestion.toLowerCase());
            if (distance < bestDistance && distance < 4) {
                bestDistance = distance;
                bestMatch = brand;
            }
        });
        return bestMatch;
    }

    /**
     * Calculates Levenshtein distance between two strings for fuzzy matching
     * @param {string} a - First string
     * @param {string} b - Second string
     * @returns {number} - The edit distance between strings
     */
    function levenshteinDistance(a, b) {
        if (a.length === 0) return b.length;
        if (b.length === 0) return a.length;

        const matrix = [];

        // Initialize matrix
        for (let i = 0; i <= b.length; i++) {
            matrix[i] = [i];
        }

        for (let j = 0; j <= a.length; j++) {
            matrix[0][j] = j;
        }

        // Fill matrix
        for (let i = 1; i <= b.length; i++) {
            for (let j = 1; j <= a.length; j++) {
                const cost = a[j - 1] === b[i - 1] ? 0 : 1;
                matrix[i][j] = Math.min(
                    matrix[i - 1][j] + 1,      // deletion
                    matrix[i][j - 1] + 1,      // insertion
                    matrix[i - 1][j - 1] + cost  // substitution
                );
            }
        }

        return matrix[b.length][a.length];
    }

    // Mustache templates for rendering suggestions and empty results
    const suggestionTemplate = `<li class="border-b--xs border-t u-border--xs border-color-ea ui-list__item u-list-style--none tt-suggestion tt-selectable">
                                    <a href="{{href}}" class="link link--noline">
                                        <p class="u-padding-l--lg u-margin-tb--lg u-padding-r--lg u-text--xl">
                                            {{suggestion}}
                                            {{#isBrand}}
                                            &nbsp;<i class="u-text--sm u-color--grey">{{#isWomens}}<rev:t>in womens</rev:t>{{/isWomens}}{{^isWomens}}<rev:t>in mens</rev:t>{{/isWomens}}<i>
                                            {{/isBrand}}
                                        </p>
                                    </a>
                                </li>`;

    const emptyTemplate = `<div class="empty-message">
                            <p>No results found for "{{query}}"</p>
                            {{#suggestion}}
                            <p>Did you mean <a class="suggestion-link" data-suggestion="{{suggestion}}" href="{{suggestionUrl}}">{{suggestion}}</a>?</p>
                            {{/suggestion}}
                        </div>`;
    const headRecentTemplate = `<p id="js-search-hed-target" class="product-recs__hed h4">Recent Searches</p>`;
    const headRecommendedTemplate = `<p id="js-search-hed-target" class="product-recs__hed h4">Designers You'll Love</p>`;

    const recommendedSuggestionTemplate = `<li class="border-b--xs border-t u-border--xs border-color-ea ui-list__item u-list-style--none tt-suggestion tt-selectable">
                                                <a href="{{href}}" class="link link--noline u-padding-l--xxl u-block">
                                                    <p class="u-padding-l--lg u-margin-tb--lg u-padding-r--lg u-text--lg">
                                                    <span class="u-margin-l--xs">{{searchText}}</span>
                                                    </p>
                                                </a>
                                            </li>`;

    /*
    * Custom search function for substring matching with priority for prefix matches
    * @param {Array} strs - Array of brand objects to search
    * @returns {Function} - Function that finds matches for a query
    */
    const substringMatcher = function (strs) {
        return function findMatches(query, cb) {
            // Array to hold matches
            const matches = [];

            // Return empty array if query is empty
            if (query === '') {
                return cb(matches);
            }

            // Regex for matching
            const substrRegex = new RegExp(query, 'i');
            // Regex for matching at the beginning of string
            const prefixRegex = new RegExp('^' + query, 'i');

            // Iterate through brands
            $.each(strs, function (i, brand) {
                // Check if name contains the substring
                if (substrRegex.test(brand.suggestion)) {
                    // Add highlighting data
                    const highlighted = brand.suggestion.replace(
                        new RegExp('(' + query + ')', 'ig'),
                        '<span class="match">$1</span>'
                    );

                    // Clone brand object and add highlighting
                    const match = $.extend({}, brand, {
                        highlightedName: highlighted,
                        // Set isPrefixMatch flag if the brand name starts with the query
                        isPrefixMatch: prefixRegex.test(brand.suggestion)
                    });

                    matches.push(match);
                }
            });

            // Sort matches to prioritize prefix matches
            matches.sort((a, b) => {
                // If one is a prefix match and the other isn't, the prefix match comes first
                if (a.isPrefixMatch && !b.isPrefixMatch) return -1;
                if (!a.isPrefixMatch && b.isPrefixMatch) return 1;

                // If both are prefix matches or both are not, sort alphabetically
                return a.suggestion.localeCompare(b.suggestion);
            });

            // Update stats
            $('#search-stats').text(matches.length + ' brands found').show();
            if (!query) {
                $('#search-stats').hide();
            }

            cb(matches);
        };
    };

    // Initialize Typeahead with multiple datasets
    $(typeaheadInput).typeahead(
        {
            hint: false,
            highlight: true,
            autoselect: false,
            minLength: 0,
            classNames: {
                menu: 'field__input-search-results',
                highlight: 'site-search__highlight'
            }
        },
        {
            // Dataset 1: Text suggestions based on user input
            name: 'textSuggestions',
            source: substringMatcher(designerBrands),
            display: 'suggestion',
            limit: 10,
            templates: {
                suggestion: function (data) {
                    const query = $('#search-designer-input').val();
                    const encodedValue = encodeURIComponent(query);
                    const url = data.suggestionUrl.replace(/&q=&/, `&q=${encodedValue}&`);
                    return Mustache.render(suggestionTemplate, {
                        href: url,
                        suggestion: data.suggestion,
                        isBrand: data.termType === "Brand",
                        isWomens: /^w.*/i.test(data.gender)
                    });
                },
                empty: function (context) {
                    const query = $('#search-designer-input').val();
                    const bestMatch = findClosestMatch(query);
                    return Mustache.render(emptyTemplate, {
                        query: query,
                        suggestion: bestMatch ? bestMatch.suggestion : null,
                        suggestionUrl: bestMatch ? bestMatch.suggestionUrl : '#',
                    });
                },
            }
        },
        {
            // Dataset 2: Recent searches (shown when search field is empty)
            name: 'recent',
            display: function (obj) {
                return obj.searchText;
            },
            source: function (query, callback) {
                // hide dataset when user starts typing
                const recentOnInput = query === '' ? recentSearchJson : JSON.parse('[]');
                callback(recentOnInput);
            },
            templates: {
                header: function () {
                    return headRecentTemplate;
                },
                suggestion: function (data) {
                    return Mustache.render(rcProps.search.recentSearchTemplate, data);
                }
            },
            limit: rcProps.recentSearchLimit
        },
        {
            // Dataset 3: Recommended searches (shown when search field is empty)
            name: 'recommended',
            display: function (obj) {
                return obj.searchText;
            },
            source: function (query, callback) {
                // hide dataset when user starts typing
                const recommendedOnInput = query === '' ? recommendedSearchJson : '[]';
                callback(JSON.parse(recommendedOnInput));
            },
            templates: {
                header: function () {
                    return headRecommendedTemplate;
                },
                suggestion: function (data) {
                    return Mustache.render(recommendedSuggestionTemplate, data);
                }
            },
            limit: rcProps.recentSearchLimit
        },
    );

    /**
     * Shows search results and hides other suggestion types
     * @param {jQuery} suggestionList - jQuery object for suggestion list
     */
    const hideOriginalSuggestions = (suggestionList) => {
        suggestionList.show();
    };

    /**
     * Shows original suggestion types and hides search results
     * @param {jQuery} suggestionList - jQuery object for suggestion list
     */
    const showOriginalSuggestions = (suggestionList) => {
        suggestionList.hide();
    };

    // Handle input changes to show/hide appropriate suggestion types
    $(typeaheadInput).on('input', function (e) {
        const suggestionList = $('.field__input-search-results .tt-dataset-textSuggestions');

        if ($(e.target).val().length > 0) {
            hideOriginalSuggestions(suggestionList);
        } else {
            showOriginalSuggestions(suggestionList);
        }
    });

    // Handle selection from typeahead
    $(typeaheadInput).on('typeahead:select', function (ev, suggestion) {
        window.location.href = !!suggestion.suggestionUrl ? suggestion.suggestionUrl : suggestion.href;
    });

    // Reset stats when input is cleared
    $('.typeahead').on('keyup', function () {
        if ($(this).val() === '') {
            $('#search-stats').hide();
        }
    });

    // Hide suggestion list on initial render
    $(typeaheadInput).on('typeahead:render', function (event, suggestions) {
        const suggestionList = $('.field__input-search-results .tt-dataset-textSuggestions');
        suggestionList.hide();
    });
};

/**
 * Merges default and provided options
 * @param {jQuery} $el - Button/element that opens search dialog
 * @param {Object} [options={}] - Any options provided upon initialization
 * @return {Object} Final options object
 */
const mergeOptions = ($el, options = {}) => {
    return $.extend({}, DEFAULTS, options);
};

/**
 * Closes the search dialog
 * @param {jQuery} $el - Trigger element
 * @param {Object} opts - Options object
 */
const closeSearchDialog = ($el, opts) => {
    const { searchDialog, typeaheadInput } = opts;
    const searchFormDialogActive = $(searchDialog).attr('aria-modal') === 'true';

    if (searchFormDialogActive) {
        $(searchDialog).attr('aria-modal', false);
        $('.form-search-overlay').remove();
        $(searchDialog).hide();
        enableBodyScroll($('.site-search__results')[0]);
        $($el).trigger('focus');
    }
};

/**
 * Opens the search dialog
 * @param {jQuery} $el - Trigger element
 * @param {Object} opts - Options object
 */
const showSearchDialog = ($el, opts) => {
    const { searchDialog, typeaheadInput } = opts;
    const searchFormDialogNotActive = $(searchDialog).attr('aria-modal') === 'false';

    if (searchFormDialogNotActive) {
        $(searchDialog).appendTo('body');
        $(searchDialog).show();

        // Initialize typeahead if not already done
        if (!$(typeaheadInput).hasClass('tt-input')) {
            initSearch($el, opts);
        }

        $(typeaheadInput).trigger('focus');
        $(searchDialog).attr('aria-modal', true);

        // Add overlay with click handler to close dialog
        $(`<div class='form-search-overlay'></div>`).appendTo('body').on('click', function () {
            closeSearchDialog($el, opts);
        });

        // Prevent body scrolling while dialog is open
        disableBodyScroll($('.site-search__results')[0]);

        // Trap keyboard focus within dialog
        Keyboard.trapFocus($(searchDialog));
    }
};

/**
 * Shows the clear and submit buttons
 * @param {string} clearButton - Clear button selector
 * @param {string} submitButton - Submit button selector
 */
const showControls = (clearButton, submitButton) => {
    $(clearButton).show();
    $(submitButton).show();
};

/**
 * Hides the clear and submit buttons
 * @param {string} clearButton - Clear button selector
 * @param {string} submitButton - Submit button selector
 */
const hideControls = (clearButton, submitButton) => {
    $(clearButton).hide();
    $(submitButton).hide();
};

/**
 * Toggles visibility of control buttons based on input state
 * @param {jQuery} $el - Trigger element
 * @param {Object} opts - Options object
 */
const toggleControls = ($el, opts) => {
    const { clearButton, submitButton, typeaheadInput } = opts;

    if ($(typeaheadInput).val().length > 0) {
        showControls(clearButton, submitButton);
    } else {
        hideControls(clearButton, submitButton);
    }
};

/**
 * Clears the search input and resets the UI
 * @param {jQuery} $el - Trigger element
 * @param {Object} opts - Options object
 */
const clearSearch = ($el, opts) => {
    const { typeaheadInput, clearButton, submitButton } = opts;

    $(typeaheadInput).typeahead('val', '');
    $(typeaheadInput).trigger('focus');
    hideControls(clearButton, submitButton);
};

/**
 * Sets up event listeners for the search dialog
 * @param {jQuery} $el - Trigger element
 * @param {Object} opts - Options object
 */
const setEventListeners = ($el, opts) => {
    const { closeButton, typeaheadInput, clearButton } = opts;

    // Click on element opens search dialog
    $el.on('click', () => showSearchDialog($el, opts));

    // Click on close button closes dialog
    $(closeButton).on('click', () => closeSearchDialog($el, opts));

    // Input events toggle visibility of controls
    $(typeaheadInput).on('input', () => toggleControls($el, opts));

    // Click on clear button resets search
    $(clearButton).on('click', () => clearSearch($el, opts));
};

/**
 * Main plugin function - initializes the designer search functionality
 * @param {HTMLElement} el - Element that triggers the search dialog
 * @param {Object} options - Configuration options
 */
export default function searchDesigner(el, options = {}) {
    const $el = $(el);
    const opts = mergeOptions($el, options);
    setEventListeners($el, opts);
}

// Register as a jQuery plugin
registerJQueryPlugin(PLUGIN_NAME, searchDesigner);


// WEBPACK FOOTER //
// ./src/scripts/entry-points/designers/search-designer.js